'use client'

import React, { useEffect, useRef, useState, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Components from '../components'

interface ImageCarouselProps {
  images: Array<{
    src: string
    alt: string
    title?: string
    description?: string
    isVideo?: boolean
    poster?: string
  }>
  className?: string
}

const SWIPE_THRESHOLD = 50

export default function ImageCarousel({ images, className = '' }: ImageCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [videoOpen, setVideoOpen] = useState(false)
  const intervalRef = useRef<number | null>(null)
  const pointerStartX = useRef<number | null>(null)
  const pointerDeltaX = useRef(0)
  const suppressClick = useRef(false)

  const nextImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
  }, [images.length])

  const prevImage = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length)
  }, [images.length])

  const onPointerDown = (e: React.PointerEvent) => {
    // Don't start a swipe from interactive controls or the video trigger
    const target = e.target as HTMLElement
    if (target.closest('[data-carousel-control], [data-video-trigger], button, a')) {
      return
    }
    pointerStartX.current = e.clientX
    pointerDeltaX.current = 0
    suppressClick.current = false
    setPaused(true)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (pointerStartX.current == null) return
    pointerDeltaX.current = e.clientX - pointerStartX.current
    if (Math.abs(pointerDeltaX.current) > 8) {
      suppressClick.current = true
    }
  }

  const onPointerUp = () => {
    if (pointerStartX.current == null) {
      setPaused(videoOpen)
      return
    }

    const delta = pointerDeltaX.current
    pointerStartX.current = null
    pointerDeltaX.current = 0

    if (Math.abs(delta) >= SWIPE_THRESHOLD && images.length > 1 && !videoOpen) {
      if (delta < 0) nextImage()
      else prevImage()
    }

    setPaused(videoOpen)
  }

  useEffect(() => {
    if (!images || images.length <= 1 || paused || videoOpen) return
    intervalRef.current = window.setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
    }, 5000)
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current)
    }
  }, [images, paused, videoOpen])

  if (!images || images.length === 0) {
    return null
  }

  const currentImage = images[currentIndex]
  const videoDataPath = currentImage.src.startsWith('/assets/')
    ? currentImage.src
        .replace('/assets/creative-projects/', 'videos/')
        .replace('/assets/', 'videos/')
    : currentImage.src

  return (
    <div
      className={`relative w-full overflow-hidden bg-[var(--brutal-ink)] touch-pan-y ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        if (!videoOpen) setPaused(false)
        pointerStartX.current = null
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="relative w-full aspect-[4/5] sm:aspect-[3/4] md:aspect-video">
        {currentImage.isVideo ? (
          <Components.VideoModal
            dataPath={videoDataPath}
            alt={currentImage.alt}
            poster={currentImage.poster}
            className="absolute inset-0 w-full h-full"
            requireAuth={false}
            onOpenChange={(open) => {
              setVideoOpen(open)
              setPaused(open)
              // Cancel any in-progress swipe so opening video never advances the slide
              pointerStartX.current = null
              pointerDeltaX.current = 0
              suppressClick.current = false
            }}
          >
            <div
              data-video-trigger
              className="absolute inset-0 w-full h-full"
              onClick={(e) => {
                if (suppressClick.current) {
                  e.preventDefault()
                  e.stopPropagation()
                }
              }}
            >
              <img
                src={currentImage.poster || currentImage.src}
                alt={currentImage.alt}
                className="absolute inset-0 w-full h-full object-cover object-[center_25%]"
                draggable={false}
              />
              <div className="absolute inset-0 bg-black/35 flex items-center justify-center pointer-events-none">
                <div className="bg-white border-2 border-[var(--brutal-ink)] rounded-full p-4 shadow-[var(--brutal-shadow-sm)]">
                  <svg className="w-8 h-8 text-[var(--brutal-ink)]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
          </Components.VideoModal>
        ) : (
          <img
            src={currentImage.src}
            alt={currentImage.alt}
            className="absolute inset-0 w-full h-full object-cover object-[center_25%]"
            draggable={false}
          />
        )}
      </div>

      {images.length > 1 && (
        <>
          <Button
            type="button"
            data-carousel-control
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation()
              prevImage()
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/70 hover:bg-black/90 text-white border border-white/40 w-9 h-9 md:w-10 md:h-10"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          <Button
            type="button"
            data-carousel-control
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation()
              nextImage()
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/70 hover:bg-black/90 text-white border border-white/40 w-9 h-9 md:w-10 md:h-10"
            aria-label="Next image"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>

          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-black/70 text-white px-2 py-1 rounded-full text-xs border border-white/40 md:hidden pointer-events-none">
            ← Swipe →
          </div>

          <div className="absolute bottom-3 right-3 z-20 bg-black/80 text-white px-2 py-1 rounded-full text-xs border border-white/40 pointer-events-none">
            {currentIndex + 1} / {images.length}
          </div>

          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                type="button"
                data-carousel-control
                onClick={(e) => {
                  e.stopPropagation()
                  setCurrentIndex(index)
                }}
                aria-label={`Go to image ${index + 1} of ${images.length}`}
                aria-current={index === currentIndex ? true : undefined}
                className={`h-2.5 w-2.5 rounded-full border-2 border-[var(--brutal-ink)] transition-colors ${
                  index === currentIndex ? 'bg-[var(--brutal-mustard)]' : 'bg-white/70'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
